import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeroIllustration } from '../components/mte/hero-asset-img';
import { Layout, Header, Card, Button } from '../components/mte/shell';
import { HERO_CELLS, type HeroCell, type HeroId } from '../components/mte/placeholders';
import { loadProgress } from '../lib/mission-progress';
import { getActiveProfile } from '../lib/profiles';
import styles from './CarteRentree.module.css';

/**
 * CarteRentree - ecran 10/11 (D-NX-073, livre 2026-09-12).
 *
 * Carte visuelle des 15 étapes de la Mission Rentrée, accessible depuis
 * l'ecran 9 (entree MR) ou la mission-card du dashboard.
 *
 * Route canon : `/enfant/mission/rentree/carte/`
 * (cf. `mte-architecture/04-routing.md` 6.2).
 *
 * Source spec Boussole :
 *  - `mte-product/05-dashboard-map.md` (5 jalons 5/10/15, 7 MR sur carte)
 *  - `mte-product/02-user-flows.md` (B4 - flux MR entree -> Carte -> etape)
 *
 * Marqueurs canon respectes :
 *  - 6 heros fixes (D-NX-038) via HERO_CELLS
 *  - Sabio mentor UNIVERSEL, NON selectionnable (D-NX-042) - non utilise ici
 *  - Mundo unique partage (D-NX-036 / M6)
 *  - Phrase d'entree canon (immuable) referencee en tete
 *  - Carte generee par l'interface depuis l'etat - JAMAIS image fixe hardcodee
 *    avec nombres/etats graves (D-NX-036 / M6, 9 `05-dashboard-map.md`)
 *  - Jalons 5/10/15 = evolution visuelle SOBRE du heros, sans cinematique
 *    (D-NX-036 / M3, M3b)
 *  - Pas d'ouverture automatique du Parcours Annuel a la fin (D-NX-036 / M4)
 *  - Pas de % ni note affiche a l'enfant (D-OB-03)
 *  - AppBar Sortie parent TOUJOURS visible (D-UF-03)
 *  - Tap targets >= --touch-xl (96x96) sur cartes etapes (D-PAL-CL-03)
 *  - prefers-reduced-motion respecte (tokens.css gerent deja durations -> 0)
 *
 * Persistance Vague 2 (ANA-DIRECT 09:55) :
 *  - currentStep vient de localStorage (loadProgress)
 *  - Default fallback = mock etape 3 si pas de sauvegarde
 *  - CTA primaire -> /enfant/mission/rentree/etape/ (Vague 2 - wire flow)
 *
 * Visual rebrand Vague 3 (ANA-DIRECT 13/09 ~22:15) :
 *  - Carte SVG château+chemin+15 cercles extraite du mockup Wave 3
 *    (cf. `mte-design/mockups/w3-carte-15-etapes.html` L149-196)
 *  - Step icons SVG inline (done checkmark / current star / locked padlock)
 *    substituindo os emojis Unicode '✓' / '★' / '🔒'
 *  - Estados done = #3F9D6E (skill-solid-500), current = #D4A93A
 *    (world-light-500) com halo, locked = #E2E4EA (world-stone-100)
 *
 * Etats carte-etape :
 *  - `done`      : franchie, marqueur plein + couleur solid
 *  - `current`   : en cours, halo cristal + bordure accent + taille legerement
 *                  augmentee
 *  - `locked`    : à venir, fond discret + icone cadenas sobre (non cliquable
 *                  en Vague 2 - affichage pedagogique uniquement)
 */
type StepState = 'done' | 'current' | 'locked';

interface StepMock {
  number: number;
  state: StepState;
  /** Jalon si cette etape est un jalon 5/10/15, sinon null. */
  milestone: 5 | 10 | 15 | null;
}

const TOTAL_STEPS = 15;

/** Genere le tableau des 15 etapes selon l'etape courante. */
function buildSteps(currentStep: number, completed: ReadonlyArray<number>): ReadonlyArray<StepMock> {
  const completedSet = new Set(completed);
  const out: StepMock[] = [];
  for (let n = 1; n <= TOTAL_STEPS; n += 1) {
    let state: StepState;
    if (n < currentStep || completedSet.has(n)) {
      state = 'done';
    } else if (n === currentStep) {
      state = 'current';
    } else {
      state = 'locked';
    }
    const milestone: 5 | 10 | 15 | null =
      n === 5 || n === 10 || n === 15 ? (n as 5 | 10 | 15) : null;
    out.push({ number: n, state, milestone });
  }
  return out;
}

const MILESTONES: ReadonlyArray<5 | 10 | 15> = [5, 10, 15];
const ENTRANCE_PHRASE = 'Réveille tes pouvoirs pour bien commencer l\u2019annee\u00a0!'; // canon immuable (ref.)

const HERO_BY_ID: ReadonlyMap<HeroId, HeroCell> = new Map(
  HERO_CELLS.filter((c) => c.id !== 'sage').map((c) => [c.id, c]),
);

function getHero(id: HeroId | null): HeroCell {
  if (!id) {
    return HERO_CELLS[0] as HeroCell;
  }
  const cell = HERO_BY_ID.get(id);
  if (!cell) {
    return HERO_CELLS[0] as HeroCell;
  }
  return cell;
}

function milestoneState(
  step: number,
  milestone: 5 | 10 | 15,
): 'reached' | 'next' | 'pending' {
  if (step >= milestone) return 'reached';
  // Cherche le prochain jalon > step
  const upcoming = MILESTONES.find((m) => m > step);
  return upcoming === milestone ? 'next' : 'pending';
}

// SVG icons inline pour les etats step (Vague 3 - ANA-DIRECT 13/09 ~22:15).
// Le dispatch a autorise la creation in-code sans demander de nouveaux assets.
function StepIconDone(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-testid="step-icon-done"
    >
      <polyline points="5 12 10 17 19 8" />
    </svg>
  );
}

function StepIconCurrent(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      data-testid="step-icon-current"
    >
      <path d="M12 2 L15 9 L22 9 L17 14 L19 22 L12 18 L5 22 L7 14 L2 9 L9 9 Z" />
    </svg>
  );
}

function StepIconLocked(): JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
      data-testid="step-icon-locked"
    >
      <rect x="6" y="11" width="12" height="10" rx="1.5" fill="currentColor" fillOpacity="0.2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function StepIcon({ state }: { state: StepState }): JSX.Element {
  if (state === 'done') return <StepIconDone />;
  if (state === 'current') return <StepIconCurrent />;
  return <StepIconLocked />;
}

export default function CarteRentree(): JSX.Element {
  const active = getActiveProfile();
  const hero = getHero(active?.heroId ?? null);
  const [currentStep, setCurrentStep] = useState<number>(3);
  const [completedSteps, setCompletedSteps] = useState<ReadonlyArray<number>>([1, 2]);

  // Charger depuis localStorage au mount (Vague 2 - ANA-DIRECT 09:55).
  useEffect(() => {
    const progress = active ? loadProgress(active.id) : loadProgress('default');
    setCurrentStep(progress.currentStep);
    setCompletedSteps(progress.completedSteps);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = buildSteps(currentStep, completedSteps);

  const appBar = (
    <Header
      context="enfant"
      user={
        <div className={styles.appBar} data-testid="app-bar">
          <div className={styles.appBarIdentity}>
            <HeroIllustration cell={hero} pose="standing" />
            <p className={styles.appBarHello}>
              <span className={styles.appBarHelloAccent}>{active?.firstName ?? "Aventurier"}</span>
              {' . '}
              {hero.label}
            </p>
          </div>
          <Link
            to="/enfant/aventure/"
            className={styles.sortieLink}
            data-action="sortie-parent"
            aria-label="Sortie parent"
          >
            <Button tone="ghost" size="md" context="enfant">
              Sortie parent
            </Button>
          </Link>
        </div>
      }
    />
  );

  return (
    <Layout context="enfant" header={appBar} width="child">
      <div className={styles.eyebrowRow}>
        <p className={styles.eyebrow}>Carte de la Mission Rentrée</p>
        <p className={styles.screenLabel} aria-label="Écran 10 sur 11">10 / 11</p>
      </div>

      <section className={styles.hero} aria-labelledby="carte-rentree-title">
        <div className={styles.heroIllustration} aria-hidden="true">
          <HeroIllustration cell={hero} pose="celebrate" />
        </div>
        <div className={styles.heroCopy}>
          <h1 id="carte-rentree-title" className={styles.title}>Ta carte des 15 étapes</h1>
          <p className={styles.subtitle}>
            {ENTRANCE_PHRASE}
          </p>
          <p className={styles.counter} data-testid="carte-counter">
            <span className={styles.counterAccent}>Étape {currentStep}</span>
            {' sur '}{TOTAL_STEPS}
          </p>
        </div>
      </section>

      <Card>
        <div className={styles.map} role="group" aria-label="Carte des 15 étapes de la Mission Rentrée">
          {/* Carte SVG château+chemin+15 cercles — extraite verbatim du mockup Wave 3
              (`mte-design/mockups/w3-carte-15-etapes.html` L149-196). Decorativa;
              la semantique de chaque étape reste portee par la <ol> ci-dessous. */}
          <img
            src="/world/carte-15-etapes.svg"
            alt=""
            role="presentation"
            className={styles.mapSvg}
            data-testid="carte-map-svg"
          />
          <ol className={styles.stepsGrid} data-testid="steps-grid">
            {steps.map((step) => (
              <li
                key={step.number}
                className={styles.stepItem}
                data-step={step.number}
                data-state={step.state}
                aria-current={step.state === 'current' ? 'step' : undefined}
                aria-label={
                  step.state === 'done'
                    ? `Étape ${step.number} : franchie`
                    : step.state === 'current'
                      ? `Étape ${step.number} : en cours`
                      : `Etape ${step.number} : à venir`
                }
              >
                <StepCell step={step} currentStep={currentStep} />
              </li>
            ))}
          </ol>
        </div>
      </Card>

      <Card>
        <div className={styles.legend} data-testid="legend">
          <p className={styles.legendEyebrow}>Légende</p>
          <ul className={styles.legendList}>
            <li className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                data-state="done"
                aria-hidden="true"
              />
              <span>Franchie</span>
            </li>
            <li className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                data-state="current"
                aria-hidden="true"
              />
              <span>En cours</span>
            </li>
            <li className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                data-state="locked"
                aria-hidden="true"
              />
              <span>À venir</span>
            </li>
          </ul>
          <p className={styles.legendNote}>
            Les étapes <strong>5</strong>, <strong>10</strong> et <strong>15</strong> sont des jalons -
            ton héros évolue visuellement à chaque jalon franchi.
          </p>
        </div>
      </Card>

      <div className={styles.actions}>
        <Link
          to="/enfant/mission/rentree/etape/"
          data-action="continuer-depuis-carte"
        >
          <Button
            tone="primary"
            size="xl"
            context="enfant"
            block
          >
            {`Continuer l\u2019étape ${currentStep}`}
          </Button>
        </Link>
        <Link to="/enfant/aventure/" data-action="retour-monde">
          <Button tone="ghost" size="md" context="enfant" block>
            Retour à la carte du monde
          </Button>
        </Link>
      </div>
    </Layout>
  );
}

function StepCell({ step, currentStep }: { step: StepMock; currentStep: number }): JSX.Element {
  return (
    <div className={styles.stepCell}>
      {step.milestone !== null ? (
        <span
          className={styles.milestoneMarker}
          data-milestone={step.milestone}
          data-milestone-state={milestoneState(currentStep, step.milestone)}
          aria-label={`Jalon ${step.milestone}`}
        />
      ) : (
        <span className={styles.milestoneSpacer} aria-hidden="true" />
      )}
      <span className={styles.stepNumber} aria-hidden="true">
        {step.number}
      </span>
      <span className={styles.stepIcon} aria-hidden="true">
        <StepIcon state={step.state} />
      </span>
    </div>
  );
}
